import React, {
  forwardRef,
  KeyboardEvent,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { fnGetDataSet } from "@shared/api/restApi";
import SearchPanel from "../SearchPanel";
import Button from "@mui/material/Button";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import CommonSpinner from "@widgets/CommonSpinner";
import { PopupSettings } from "@widgets/commonpopup/PopupSettings";

interface SinglePopupProps {
  code: string;
  selectedData?: any;
  modalRef?: any;
  autoSearch?: boolean;
}

const SinglePopup = forwardRef(
  (
    { code, selectedData, modalRef, autoSearch = true }: SinglePopupProps,
    ref
  ) => {
    const { userInfo } = global();
    const searchRef = useRef<any>(null);
    const gridApiRef = useRef<any>(null);
    const returnDataRef = useRef<any[]>();

    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [columns, setColums] = useState<any[]>([]);
    const [dataSource1, setDataSource1] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useImperativeHandle(ref, () => ({
      getSelectedRow: () => returnDataRef.current,
      onSearch,
    }));

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(code, {});
          const excludedKeys = PopupSettings[code]?.excludeBindKeys ?? [];
          const safeSelectedData = Object.fromEntries(
            Object.entries(selectedData ?? {}).filter(
              ([key]) => !excludedKeys.includes(key)
            )
          );
          const mergedData = { ...rtn.formData, ...safeSelectedData };
          setFormData(mergedData);
          setFormControlData(rtn.formControlData);
          setColums(rtn.grid.grid1);
          if (mergedData && mergedData.str_search) {
            // 초기화 시 조회조건 있으면 → SearchPanel(조회조건)에 포커스 유지
            onSearch(mergedData, true);
          } else {
            // 초기화 시 조회조건 없으면 → 자동검색 후 그리드로 포커스 이동
            if (autoSearch) onSearch(mergedData, false);
          }
        } catch (error) {}
      };
      fetchform();
    }, [userInfo]);

    // 그리드에 행이 렌더될 때까지 기다렸다가 첫 행을 선택/포커스한다.
    const focusGridFirstRow = (attempt: number = 0) => {
      const api = gridApiRef.current;
      if (!api) return;
      if ((api.getDisplayedRowCount?.() ?? 0) > 0) {
        api.selectAndFocusRow(0, false);
        return;
      }
      if (attempt < 30) {
        requestAnimationFrame(() => focusGridFirstRow(attempt + 1));
      }
    };

    const onSearch = async (mergedData: any, keepFocus: boolean = false) => {
      setIsLoading(true);
      returnDataRef.current = undefined;
      let rowCount = 0;
      const fetchdatasource = async () => {
        try {
          const params = {
            id: code.toLowerCase() + "_01",
            params: mergedData ? mergedData : searchRef.current.getFormdata(),
          };

          setDataSource1([]);
          returnDataRef.current = undefined;
          const data: any = await fnGetDataSet(params);
          const rows = data[0] || [];
          rowCount = rows.length;
          setDataSource1(rows);
          gridApiRef.current.resetUpdate();
        } catch (error) {}
      };
      await fetchdatasource();
      gridApiRef.current.resetUpdate();
      setIsLoading(false);
      // 조회 결과가 있고 그리드로 이동해야 하면 첫 행 포커스,
      // 그 외(조회조건 유지 or 결과 0건)에는 SearchPanel 포커스 유지
      if (!keepFocus && rowCount > 0) {
        focusGridFirstRow();
      } else {
        searchRef.current?.focusLastInput?.();
      }
    };

    const setSelectedRow = (rowData?: any) => {
      const selectedRow = rowData ? [rowData] : undefined;
      returnDataRef.current = selectedRow;
      return selectedRow;
    };

    const confirmSelectedRow = (rowData?: any) => {
      const selectedRow = setSelectedRow(rowData);
      modalRef.current?.handleConfirm(true, selectedRow);
    };

    const onRowClicked = (rowData: any) => {
      setSelectedRow(rowData);
    };

    const onRowDoubleClicked = (e: any) => {
      confirmSelectedRow(e.data);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        const selectedRows = gridApiRef.current?.getSelectedRows?.();
        const gridData = selectedRows?.[0];
        confirmSelectedRow(gridData);
      }
    };

    return (
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        {isLoading && <CommonSpinner isLoading={isLoading} />}
        <div>
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
            popupRef={ref}
          ></SearchPanel>
        </div>
        <div style={{ textAlign: "center" }}>
          <Button
            type="button"
            variant="outlined"
            onClick={() => {
              onSearch(undefined);
            }}
            sx={{
              width: 100,
              marginTop: 2,
            }}
          >
            조회
          </Button>
        </div>
        <div
          style={{
            flex: 1,
            overflow: "hidden",
            marginTop: "10px",
            minHeight: 0,
          }}
          onKeyDown={handleKeyDown}
        >
          <SnGrid
            ref={gridApiRef}
            columnConfig={columns}
            rowData={dataSource1}
            etcProps={{
              rowSelection: "single",
              isEditable: false,
              // 조회 직후 그리드가 포커스를 뺏지 않도록 항상 억제하고,
              // 포커스 이동은 팝업(onSearch)에서 명시적으로 제어한다.
              suppressInitialCellFocus: true,
            }}
            events={{
              onFocusedRowChanged: onRowClicked,
              onRowDoubleClicked: onRowDoubleClicked,
            }}
          />
        </div>
      </div>
    );
  }
);

export default SinglePopup;
